// src/training/mep/MepForm.jsx

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { 
    Box, 
    Button, 
    Typography, 
    CircularProgress,
    Paper,
    IconButton,
    Chip,
    Stack,
    Alert
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    CloudUpload as CloudUploadIcon,
    CalendarToday as CalendarIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

import TrainingFormHeader from '../../TrainingFormHeader';
import TrainingFormSection from '../../TrainingFormSection';
import SignatureModalWithAuto from '../../../common/SignatureModalWithAuto';

import {
    saveMepForm,
    markMepFormStatus,
    getAllForms,
} from '../../../../services/mepFormService';

import {
    STATUS_FINISH,
    STATUS_PROCESS,
    STATUS_SEND,
} from '../../../../config/constants';

import axiosInstance from '../../../../axiosInstance';
import { hasSignatureContent } from '../../../../utils/utils';
import { localDateString } from '../../../../utils/localDate';

// Pulsing upload button style
const pulsingUploadButton = {
    position: 'relative',
    overflow: 'visible',
    animation: 'pulse 2s ease-in-out infinite',
    '@keyframes pulse': {
        '0%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)',
        },
        '50%': {
            transform: 'scale(1.05)',
            boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)',
        },
        '100%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
        },
    },
};

/**
 * Format date helper
 */
const formatDateTime = (dateValue) => {
    if (!dateValue) return 'Not sent yet';
    
    if (typeof dateValue === 'object' && dateValue?.date) {
        return new Date(dateValue.date).toLocaleString('en-US');
    }
    
    if (dateValue) {
        return new Date(dateValue).toLocaleString('en-US');
    }
    
    return 'Not sent yet';
};

export default function MepForm() {
    const navigate = useNavigate();
    const { state } = useLocation() || {};

    const selectedTrainingDue = state?.selectedTrainingDue || null;
    const trainingDueIdFromRoute = selectedTrainingDue?.id || state?.trainingDueId || null;

    const sigPadRef = useRef(null);

    const [isUploading, setIsUploading] = useState(false);
    const [loading, setLoading] = useState(true);

    const defaultFormData = {
        operatorName: selectedTrainingDue?.fullName || '',
        trainerName: '',
        date: localDateString(),
        course: selectedTrainingDue?.course || '',
        sendBy: localStorage.getItem('userId'),
        section7: {
            raiseAndLower: false,
            travelForward: false,
            rotateLeftRight: false,
            moveBoomInOut: false,
            rotateBasket: false,
            useArticulatingControls: false,
            useOutriggers: false,
            otherEnabled: false,
            other: '',
        },
        section8: {
            nearPowerLines: false,
            fallTipHazards: false,
            controlNotInView: false,
            emergencyOperation: false,
            maxSideForce: false,
        },
        section9: {
            properMounting: false,
            directionOfTravel: false,
            weightCapacity: false,
            appropriateSpeeds: false,
            unlevelGround: false,
            properSurface: false,
            cornering: false,
            SoundingIntersections: false,
        },
        section10: {
            properParking: false,
            hazardousAreas: false,
            awareOfEmployees: false,
            otherEnabled: false,
            other: ''
        },
        section11: {
            success: false,
        },
        completed: false,
        signature: null,
        status: STATUS_PROCESS,
        trainingDueId: null,
        lastSentAt: null,
    };

    const [formData, setFormData] = useState(null);
    const [showSignatureModal, setShowSignatureModal] = useState(false);


    /**
     * Load/create MEP form from IndexedDB
     */
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const allForms = await getAllForms();
                const activeForms = allForms.filter(f => f.status !== STATUS_SEND);

                let foundForm = null;

                if (trainingDueIdFromRoute) {
                    foundForm = activeForms.find(f => f.trainingDueId === trainingDueIdFromRoute);
                    if (!foundForm) {
                        foundForm = {
                            ...defaultFormData,
                            trainingDueId: trainingDueIdFromRoute,
                            id: Date.now(),
                        };
                        await saveMepForm(foundForm);
                    }
                } else {
                    foundForm = activeForms.find(f => f.operatorName === (selectedTrainingDue?.fullName || ''));
                    if (!foundForm) {
                        foundForm = {
                            ...defaultFormData,
                            id: Date.now(),
                        };
                        await saveMepForm(foundForm);
                    }
                }

                setFormData(foundForm);
            } catch (error) {
                console.error('Error loading MEP form:', error);
                setFormData({ ...defaultFormData, id: Date.now() });
            } finally {
                setLoading(false);
            }
        })();
    }, [trainingDueIdFromRoute, selectedTrainingDue]);

    /**
     * Handle input change
     */
    const handleInputChange = useCallback((field, value) => {
        const updatedData = { ...formData, [field]: value };
        setFormData(updatedData);
        saveMepForm(updatedData);
    }, [formData]);

    /**
     * Handle section field change
     */
    const handleFieldChange = useCallback((section, field, value) => {
        const updatedData = {
            ...formData,
            [section]: {
                ...formData[section],
                [field]: value,
            }
        };
        setFormData(updatedData);
        saveMepForm(updatedData);
    }, [formData]);

    /**
     * Save signature
     */
    const handleSaveSignature = useCallback(async () => {
        if (hasSignatureContent(sigPadRef)) {
            const signatureData = sigPadRef.current.toDataURL();
            const updatedData = {
                ...formData,
                signature: signatureData,
                completed: true,
                status: STATUS_PROCESS,
            };
            setFormData(updatedData);
            await saveMepForm(updatedData);
            setShowSignatureModal(false);
        }
    }, [formData]);

    /**
     * Send form
     */
    const handleSend = useCallback(async () => {
        if (!formData.signature || !formData.completed) {
            alert('Please complete and sign the form before sending.');
            return;
        }

        setIsUploading(true);
        try {
            const payload = {
                ...formData,
                dateDue: selectedTrainingDue?.dateDue,
                dateThru: selectedTrainingDue?.dateThru,
                formType: 'mep',
            };

            const response = await axiosInstance.post('/training-due/upload', payload);

            if (response.status === 200) {
                const updatedData = {
                    ...formData,
                    status: STATUS_SEND,
                    lastSentAt: new Date().toISOString(),
                };
                await markMepFormStatus(formData.id, STATUS_SEND);
                setFormData(updatedData);
                alert('Form sent successfully!');
                navigate('/training-due');
            } else {
                throw new Error(`Server responded with status ${response.status}`);
            }
        } catch (error) {
            console.error('Error sending form:', error);
            const updatedData = {
                ...formData,
                status: STATUS_FINISH,
            };
            await markMepFormStatus(formData.id, STATUS_FINISH);
            setFormData(updatedData);
            alert('Failed to send form but saved locally.');
        } finally {
            setIsUploading(false);
        }
    }, [formData, selectedTrainingDue, navigate]);

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '100vh' 
            }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!formData) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">Failed to load form data</Alert>
            </Box>
        );
    }

    const canSend = formData.signature && formData.completed;
    const isReadyToSend = formData.status === STATUS_PROCESS && canSend;

    // Get status color
    const getStatusColor = () => {
        switch(formData.status) {
            case STATUS_PROCESS: return 'warning';
            case STATUS_SEND: return 'success';
            case STATUS_FINISH: return 'info';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100vh',
            bgcolor: 'background.default'
        }}>
            {/* Fixed Header */}
            <Paper 
                elevation={2}
                sx={{ 
                    position: 'sticky',
                    top: 0,
                    zIndex: 1100,
                    borderRadius: 0,
                    borderBottom: 1,
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                            <IconButton 
                                onClick={() => navigate('/training-due')}
                                size="large"
                                sx={{ 
                                    bgcolor: 'primary.light',
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                        bgcolor: 'primary.main',
                                    }
                                }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            
                            <Box>
                                <Typography variant="h5" fontWeight="bold">
                                    MEP Training Form
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={formData.status?.toUpperCase() || 'NEW'}
                                        color={getStatusColor()}
                                        size="small"
                                        sx={{ fontWeight: 'bold' }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        <CalendarIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                        {formData.date}
                                    </Typography>
                                    {formData.operatorName && (
                                        <Typography variant="caption" color="text.secondary">
                                            • {formData.operatorName}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </Stack>

                        {/* Send Button in Header */}
                        <Box sx={{ position: 'relative' }}>
                            <Button
                                variant="contained"
                                color={isReadyToSend ? "error" : "inherit"}
                                onClick={handleSend}
                                disabled={!canSend || isUploading}
                                startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                sx={isReadyToSend && !isUploading ? pulsingUploadButton : {}}
                            >
                                {isUploading ? 'Sending...' : 'Send'}
                            </Button>
                            {formData.lastSentAt && (
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        position: 'absolute',
                                        bottom: -20,
                                        right: 0,
                                        whiteSpace: 'nowrap',
                                        color: 'text.secondary'
                                    }}
                                >
                                    Last sent: {formatDateTime(formData.lastSentAt)}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Box>
            </Paper>

            {/* Scrollable Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                {/* Form Header */}
                <TrainingFormHeader
                    onGoBack={'#/training-due'}
                    operatorName={formData.operatorName}
                    trainerName={formData.trainerName}
                    date={formData.date}
                    onInputChange={handleInputChange}
                    readOnly={!!selectedTrainingDue}
                    maxDate={''}
                    minDate={''}
                />

                {/* Section 7 */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <TrainingFormSection
                        title="Ability to demonstrate and explain proper use of controls:"
                        fields={[
                            { key: 'raiseAndLower', label: 'Raise & Lower', type: 'checkbox' },
                            { key: 'travelForward', label: 'Travel forward & Backwards while steering', type: 'checkbox' },
                            { key: 'rotateLeftRight', label: 'Rotate left & right', type: 'checkbox' },
                            { key: 'moveBoomInOut', label: 'Move boom in & out', type: 'checkbox' },
                            { key: 'rotateBasket', label: 'Rotate Basket', type: 'checkbox' },
                            { key: 'useArticulatingControls', label: 'Use Articulating Controls', type: 'checkbox' },
                            { key: 'useOutriggers', label: 'Use Outriggers if equipped', type: 'checkbox' },
                            { key: 'otherEnabled', label: 'Other', type: 'checkbox' },
                            { key: 'other', label: 'Other', type: 'text', conditionalOn: 'otherEnabled' },
                        ]}
                        values={formData.section7}
                        onFieldChange={(key, value) => handleFieldChange('section7', key, value)}
                    />
                </Paper>

                {/* Section 8 */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                        Safety and Positioning
                    </Typography>
                    <Stack spacing={2}>
                        <TrainingFormSection
                            title=""
                            fields={[
                                { key: 'nearPowerLines', label: 'Proper positioning of equipment when within vicinity of power lines', type: 'checkboxLeft'},
                            ]}
                            values={formData.section8}
                            onFieldChange={(key, value) => handleFieldChange('section8', key, value)}
                        />
                        <TrainingFormSection
                            title=""
                            fields={[
                                { key: 'fallTipHazards', label: 'Proper positioning of equipment when near fall/tip over hazards', type: 'checkboxLeft' },
                            ]}
                            values={formData.section8}
                            onFieldChange={(key, value) => handleFieldChange('section8', key, value)}
                        />
                        <TrainingFormSection
                            title=""
                            fields={[
                                { key: 'controlNotInView', label: 'Proper position of control when not in view - regardless of distance', type: 'checkboxLeft' },
                            ]}
                            values={formData.section8}
                            onFieldChange={(key, value) => handleFieldChange('section8', key, value)}
                        />
                        <TrainingFormSection
                            title=""
                            fields={[
                                { key: 'emergencyOperation', label: 'Emergency operation to lower equipment due to power loss', type: 'checkboxLeft' }
                            ]}
                            values={formData.section8}
                            onFieldChange={(key, value) => handleFieldChange('section8', key, value)}
                        />
                        <TrainingFormSection
                            title=""
                            fields={[
                                { key: 'maxSideForce', label: 'Understanding of the maximum allowable side force (horizontal load)', type: 'checkboxLeft' }
                            ]}
                            values={formData.section8}
                            onFieldChange={(key, value) => handleFieldChange('section8', key, value)}
                        />
                    </Stack>
                </Paper>

                {/* Section 9 */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <TrainingFormSection
                        title="Demonstration of proper equipment operation:"
                        fields={[
                            { key: 'properMounting', label: 'Proper mounting and dismounting', type: 'checkbox' },
                            { key: 'directionOfTravel', label: 'Looking in the direction of travel', type: 'checkbox' },
                            { key: 'weightCapacity', label: 'Weight and personnel capacity', type: 'checkbox' },
                            { key: 'appropriateSpeeds', label: 'Appropriate speeds', type: 'checkbox' },
                            { key: 'unlevelGround', label: 'Un-level Ground & Speed bumps', type: 'checkbox' },
                            { key: 'properSurface', label: 'Proper adjustment to wet or uneven driving surface', type: 'checkbox' },
                            { key: 'cornering', label: 'Cornering', type: 'checkbox' },
                            { key: 'soundingIntersections', label: 'Sounding horn at corners and intersections', type: 'checkbox' },
                        ]}
                        values={formData.section9}
                        onFieldChange={(key, value) => handleFieldChange('section9', key, value)}
                    />
                </Paper>

                {/* Section 10 */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <TrainingFormSection
                        title="Workplace conditions:"
                        fields={[
                            { key: 'properParking', label: 'Demonstrates proper parking', type: 'checkbox' },
                            { key: 'hazardousAreas', label: 'Awareness of dangerous or hazardous areas', type: 'checkbox' },
                            { key: 'awareOfEmployees', label: 'Awareness of other employees', type: 'checkbox' },
                            { key: 'otherEnabled', label: 'Other', type: 'checkbox' },
                            { key: 'other', label: 'Other', type: 'text', conditionalOn: 'otherEnabled' },
                        ]}
                        values={formData.section10}
                        onFieldChange={(key, value) => handleFieldChange('section10', key, value)}
                    />
                </Paper>

                {/* Section 11 - Pass/Fail */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <TrainingFormSection
                        title="Evaluation Result:"
                        fields={[
                            { key: 'success', label: 'Successful completion of training (Pass)', type: 'checkbox' },
                        ]}
                        values={formData.section11}
                        onFieldChange={(key, value) => handleFieldChange('section11', key, value)}
                    />
                </Paper>

                {/* Signature Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6" fontWeight="bold">
                                Operator Signature
                            </Typography>
                            {formData.signature ? (
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                                    <CheckCircleIcon color="success" />
                                    <Typography color="success.main">
                                        Signed
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography color="text.secondary" sx={{ mt: 1 }}>
                                    Not signed yet
                                </Typography>
                            )}
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setShowSignatureModal(true)}
                        >
                            {formData.signature ? 'Update Signature' : 'Add Signature'}
                        </Button>
                    </Stack>
                    
                    {formData.signature && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <img 
                                src={formData.signature} 
                                alt="Signature" 
                                style={{ maxWidth: '300px', height: 'auto' }}
                            />
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* Signature Modal */}
            <SignatureModalWithAuto
                open={showSignatureModal}
                onClose={() => setShowSignatureModal(false)}
                sigPadRef={sigPadRef}
                employeeName={formData.operatorName || 'Operator'}
                onAccept={handleSaveSignature}
            />
        </Box>
    );
}